import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/states';

/**
 * "No rows" and "the query failed" must not render the same — kept local to
 * this module for the same reason `Pages/Fleet/components/PanelResult.tsx`
 * and `Pages/Fiscal/components/PanelResult.tsx` are each their own copy: a
 * feature's empty/error copy is its own. Under the runtime `NOBYPASSRLS` role
 * an unbound tenant read returns zero rows rather than erroring, so a broken
 * transfers query would otherwise render as "no transfers" — indistinguishable
 * from a genuinely quiet commissary.
 */
export interface PanelResultProps {
  loading: boolean;
  error: string | null;
  count: number;
  onRetry: () => void;
  subject: string;
  filtered?: boolean;
  onClearFilters?: () => void;
  emptyTitle: string;
  emptyHint?: string;
  emptyAction?: ReactNode;
  children: ReactNode;
}

export function PanelResult({
  loading,
  error,
  count,
  onRetry,
  subject,
  filtered,
  onClearFilters,
  emptyTitle,
  emptyHint,
  emptyAction,
  children,
}: PanelResultProps) {
  const { t } = useTranslation();

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50/70 p-6 text-center dark:border-red-900 dark:bg-red-950/30"
      >
        <div className="grid place-items-center gap-2">
          <Icon as={TriangleAlert} size="xl" className="text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium">{t('centralKitchen.states.loadFailed', { subject })}</p>
          <p className="max-w-lg text-sm text-ink-muted">{error}</p>
          <p className="max-w-lg text-xs text-ink-muted">{t('centralKitchen.states.loadFailedHint')}</p>
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
            <Icon as={RefreshCw} />
            {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  if (count === 0 && filtered) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={SearchX} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{t('centralKitchen.states.noMatches')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('centralKitchen.states.noMatchesHint')}</p>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t('centralKitchen.states.clearFilters')}
          </Button>
        )}
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={Inbox} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        {emptyHint && <p className="max-w-sm text-sm text-ink-muted">{emptyHint}</p>}
        {emptyAction}
      </div>
    );
  }

  return <>{children}</>;
}

export default PanelResult;
